import {
  useCallback,
  useState,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import type {
  ChangeEventHandler,
  HTMLProps,
  ReactElement,
} from 'react';

import { components as defaultComponents } from './components';
import { Dropdown } from './Dropdown';

import type {
  Components,
  FormatOptionLabel,
  GetOptionLabel,
  LoadOptions,
  OnSelect,
} from './types';

export type AutocompleteProps<Option> = {
  /**
   * Функция загрузки опций
   * @param {String} search - текущее значение поля ввода
   * @returns {Object[]} response.options - опции
   */
  loadOptions: LoadOptions<Option>;
  /**
   * Значение элемента input
   */
  value: string;
  /**
  * Обработчик изменения значения поля при ручном вводе
  */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /**
  * Обработчик изменения значения поля при выборе из меню
  * @param {String} value - текст выбранной опции
  * @param {Object} option - выбранная опция
  */
  onSelect?: OnSelect<Option>;
  /**
   * Выключено ли поле
   */
  disabled?: boolean;
  /**
   * Есть ли у поля ошибка
   */
  hasError?: boolean;
  /**
   * Есть ли у поля предупреждение
   */
  hasWarning?: boolean;

  /**
   * Дополнительные props элемента input
   */
  inputProps?: Omit<HTMLProps<HTMLInputElement>, 'value' | 'onChange' | 'disabled'>;

  /**
   * Ключ, по которому хранится текст опции
   */
  labelKey?: string;
  /**
   * Функция получения текста опции, который будет подставлен при выборе
   */
  getOptionLabel?: GetOptionLabel<Option>;
  /**
   * Функция отображения опции
   */
  formatOptionLabel?: FormatOptionLabel<Option>;
  /**
   * Переиспользуемые компоненты
   */
  components?: Partial<Components>;
};

const emptyObj = {};

export function Autocomplete<Option>({
  loadOptions,
  value,
  onChange: onChangeProp = undefined,
  onSelect: onSelectProp = undefined,
  disabled = false,
  hasError = false,
  hasWarning = false,
  inputProps = emptyObj,
  labelKey = 'label',
  getOptionLabel: getOptionLabelProp = undefined,
  formatOptionLabel: formatOptionLabelProp = undefined,
  components: componentsProp = emptyObj,
}: AutocompleteProps<Option>): ReactElement {
  const components = useMemo<Components>(() => ({
    ...defaultComponents,
    ...componentsProp,
  }), [componentsProp]);

  const valueRef = useRef<string>(value);
  valueRef.current = value;

  const [options, setOptions] = useState<Option[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const getOptionLabel = useCallback<GetOptionLabel<Option>>((option) => {
    if (getOptionLabelProp) {
      return getOptionLabelProp(option);
    }

    if (typeof option === 'string') {
      return option;
    }

    if (typeof option === 'number') {
      return String(option);
    }

    if (!option || typeof option !== 'object') {
      return '';
    }

    const label = (option as Record<string, unknown>)[labelKey];

    if (typeof label === 'string') {
      return label;
    }

    if (typeof label === 'number') {
      return String(label);
    }

    return '';
  }, [getOptionLabelProp, labelKey]);

  const formatOptionLabel = useCallback<FormatOptionLabel<Option>>((option) => {
    if (formatOptionLabelProp) {
      return formatOptionLabelProp(option);
    }

    return getOptionLabel(option);
  }, [formatOptionLabelProp, getOptionLabel]);

  const onInputFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSelect = useCallback<OnSelect<Option>>((label, option) => {
    setIsOpen(false);

    if (onSelectProp) {
      onSelectProp(label, option);
    }
  }, [onSelectProp]);

  const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    if (onChangeProp) {
      onChangeProp(event);
    }

    if (!isOpen) {
      setIsOpen(true);
    }
  }, [onChangeProp, isOpen]);

  useEffect(() => {
    (async (): Promise<void> => {
      const response = await loadOptions(value);

      if (valueRef.current === value) {
        setOptions(response.options);
      }
    })().catch((e) => {
      throw e;
    });
  }, [
    loadOptions,
    value,
  ]);

  const {
    Wrapper,
    Input,
  } = components;

  return (
    <Wrapper>
      <Input
        {...inputProps}
        disabled={disabled}
        $hasError={hasError}
        $hasWarning={hasWarning}
        value={value}
        onChange={onChange}
        onFocus={onInputFocus}
      />

      {
        isOpen && options.length > 0 && (
          <Dropdown
            components={components}
            getOptionLabel={getOptionLabel}
            formatOptionLabel={formatOptionLabel}
            options={options}
            onSelect={onSelect}
            closeMenu={closeMenu}
          />
        )
      }
    </Wrapper>
  );
}
